import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import PartnersMarquee from "./PartnersMarquee";

const POEM = {
  fr: [
    "L'âme des lieux est empreinte de paix, d'écoute et de liberté intérieure.",
    "Entre ciel et grèves, dernière pierre avant la mer et première promesse du Mont,",
    "À la croisée de la route des Ducs passant par Caen et de la Via Angelica venant du Monte Sant'Angelo en Italie, des voix sacrées s'élèvent,",
    "Du Levant au Couchant comme la course de la vie,",
    "Depuis l'Orient, au prieuré, saint Léonard nous libère de toutes nos prisons avant la traversée des eaux de la Mort vers la Jérusalem Céleste,",
    "Vers l'Occident, au mont saint Michel, l'Archange soupèse nos âmes et nous met sur le chemin de la Résurrection.",
  ],
  en: [
    "The soul of these places is imbued with peace, attentive listening, and inner freedom.",
    "Between sky and shore, the last stone before the sea and the first promise of the Mont,",
    "At the crossroads of the Dukes' route through Caen and the Via Angelica from Monte Sant'Angelo in Italy, sacred voices rise,",
    "From sunrise to sunset, as the course of a life,",
    "From the East, at the priory, saint Léonard frees us from all our prisons before the crossing of the waters of Death toward the Heavenly Jerusalem,",
    "Toward the West, at Mont Saint Michel, the Archangel weighs our souls and sets us on the path of the Resurrection.",
  ],
  it: [
    "L'anima di questi luoghi è intrisa di pace, di ascolto e di libertà interiore.",
    "Tra cielo e spiagge, ultima pietra prima del mare e prima promessa del Monte,",
    "All'incrocio della rotta dei Duchi passante per Caen e della Via Angelica proveniente dal Monte Sant'Angelo in Italia, voci sacre si elevano,",
    "Dal Levante al Tramonto come il corso della vita,",
    "Dall'Oriente, al priorato, san Leonardo ci libera da tutte le nostre prigioni prima della traversata delle acque della Morte verso la Gerusalemme Celeste,",
    "Verso l'Occidente, al Mont Saint-Michel, l'Arcangelo soppesa le nostre anime e ci pone sul cammino della Risurrezione.",
  ],
  es: [
    "El alma de estos lugares está impregnada de paz, escucha y libertad interior.",
    "Entre cielo y playas, última piedra antes del mar y primera promesa del Monte,",
    "En la encrucijada de la ruta de los Duques que pasa por Caen y la Vía Angélica que viene del Monte Sant'Angelo en Italia, se elevan voces sagradas,",
    "Del Levante al Poniente como el curso de la vida,",
    "Desde Oriente, en el priorato, san Leonardo nos libera de todas nuestras prisiones antes de la travesía de las aguas de la Muerte hacia la Jerusalén Celestial,",
    "Hacia Occidente, en el Mont Saint-Michel, el Arcángel pesa nuestras almas y nos pone en el camino de la Resurrección.",
  ],
  de: [
    "Die Seele dieser Orte ist erfüllt von Frieden, Zuhören und innerer Freiheit.",
    "Zwischen Himmel und Strand, der letzte Stein vor dem Meer und das erste Versprechen des Mont,",
    "An der Kreuzung der Herzogsroute durch Caen und der Via Angelica vom Monte Sant'Angelo in Italien erheben sich heilige Stimmen,",
    "Vom Sonnenaufgang zum Sonnenuntergang wie der Lauf des Lebens,",
    "Aus dem Osten, in der Priorei, befreit uns der heilige Leonhard von allen unseren Gefängnissen vor der Überquerung der Wasser des Todes zum Himmlischen Jerusalem,",
    "Nach Westen, am Mont Saint-Michel, wiegt der Erzengel unsere Seelen und führt uns auf den Weg der Auferstehung.",
  ],
};

const KICKER = {
  fr: "Vains · Manche · Normandie",
  en: "Vains · Manche · Normandy",
  it: "Vains · Manche · Normandia",
  es: "Vains · Manche · Normandía",
  de: "Vains · Manche · Normandie",
};

export default function Footer() {
  const { t, locale } = useI18n();
  const isFr = locale === "fr";
  const lines = POEM[locale] || POEM.fr;
  const kicker = KICKER[locale] || KICKER.fr;
  return (
    <footer
      data-testid="site-footer"
      className="mt-32 border-t"
      style={{ borderColor: "var(--border)", background: "var(--parchment-2)" }}
    >
      <PartnersMarquee />

      {/* Two-column: poem (left, tight) + contact card (right, accent block) */}
      <div className="container-prieure py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left — poem, left-aligned, tight */}
          <div className="md:col-span-7" data-testid="footer-poem">
            <div className="mb-4">
              <span className="font-caps text-[0.6rem]"
                    style={{ color: "var(--gold)", letterSpacing: "0.24em" }}>
                {kicker}
              </span>
            </div>
            <div className="font-display italic text-[0.95rem] leading-[1.45]"
                 style={{ color: "var(--ink-2)" }}>
              {lines.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < lines.length - 1 && " "}
                </span>
              ))}
            </div>
          </div>

          {/* Right — contact card */}
          <div className="md:col-span-5 flex justify-center md:justify-end" data-testid="footer-contact-wrap">
            <div
              data-testid="footer-contact"
              className="w-full max-w-sm p-7"
              style={{
                background: "var(--ink)",
                color: "var(--parchment)",
                borderRadius: "2px",
                boxShadow: "0 8px 28px rgba(20,16,12,0.25)",
              }}
            >
              <div className="font-caps text-[0.6rem] mb-3"
                   style={{ color: "var(--gold)", letterSpacing: "0.24em" }}>
                ◇ {t.footer.contact} ◇
              </div>
              <div className="space-y-2 font-body text-[0.92rem]">
                <div>{t.footer.address}</div>
                <div>
                  <a href="mailto:contact@prieure-saint-leonard.fr"
                     className="link-underline" style={{ color: "var(--parchment)" }}>
                    contact@prieure-saint-leonard.fr
                  </a>
                </div>
                <div>
                  <a href="tel:+33745200900"
                     className="link-underline" style={{ color: "var(--parchment)" }}>
                    +33 7 45 20 09 00
                  </a>
                </div>
                <div className="pt-2">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Prieur%C3%A9+Saint-L%C3%A9onard+Vains"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-google-review"
                    className="inline-flex items-center gap-2 px-4 py-2 border font-caps text-[0.6rem] transition-colors"
                    style={{ borderColor: "var(--gold)", color: "var(--gold)", letterSpacing: "0.2em" }}
                  >
                    ★ {locale === "fr" ? "LAISSEZ-NOUS UN AVIS GOOGLE" : "LEAVE US A GOOGLE REVIEW"}
                  </a>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t flex flex-wrap gap-3 items-center text-[0.65rem]"
                   style={{ borderColor: "rgba(245,240,228,0.18)" }}>
                <Link to="/mentions-legales" data-testid="footer-legal-link"
                      className="font-caps"
                      style={{ color: "rgba(245,240,228,0.78)", letterSpacing: "0.2em" }}>
                  {t.footer.legalNotice}
                </Link>
                <span style={{ color: "rgba(245,240,228,0.4)" }}>·</span>
                <Link to="/confidentialite" data-testid="footer-privacy-link"
                      className="font-caps"
                      style={{ color: "rgba(245,240,228,0.78)", letterSpacing: "0.2em" }}>
                  {t.footer.privacy}
                </Link>
                <span style={{ color: "rgba(245,240,228,0.4)" }}>·</span>
                <Link to="/rgpd" data-testid="footer-rgpd-link"
                      className="font-caps"
                      style={{ color: "rgba(245,240,228,0.78)", letterSpacing: "0.2em" }}>
                  RGPD
                </Link>
                <span style={{ color: "rgba(245,240,228,0.4)" }}>·</span>
                <Link to="/soutiens-institutionnels" data-testid="footer-soutiens-link"
                      className="font-caps"
                      style={{ color: "rgba(245,240,228,0.78)", letterSpacing: "0.2em" }}>
                  {locale === "fr" ? "Soutiens" : "Patrons"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="container-prieure py-5 text-center font-body text-sm" style={{ color: "var(--muted)" }}>
          <p className="mb-2 text-xs leading-relaxed max-w-4xl mx-auto" data-testid="footer-seo-baseline" style={{ color: "var(--muted)", opacity: 0.85 }}>
            {isFr
              ? "Prieuré saint Léonard de Vains — édifice roman millénaire (1027) en baie du Mont-Saint-Michel, étape de la route historique des Abbayes de Normandie, de Caen au Mont. Hébergement et séjours de charme, traversées de la baie, concerts, résidences d'artistes et Fêtes du Millénaire 2027 en Normandie."
              : "Priory of Saint Léonard, Vains — a thousand-year-old Romanesque landmark (1027) on the Bay of Mont-Saint-Michel, a stage on the historic route of the Abbeys of Normandy, from Caen to the Mont. Charming stays, bay crossings, concerts, artist residencies and the 2027 Millennium Feasts in Normandy."}
          </p>
          {t.footer.legal}
        </div>
      </div>
    </footer>
  );
}
